import { useCallback, useEffect, useRef, useState } from "react"

import { useTranslation } from "react-i18next"

import { Search, X } from "@/components/icons"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover"
import { useSidebar } from "@/components/ui/use-sidebar"
import { cn } from "@/lib/utils"

type SidebarSearchButtonProps = {
  className?: string
  onSearchChange: (value: string) => void
  searchValue: string
}

export function SidebarSearchButton({
  className,
  onSearchChange,
  searchValue,
}: SidebarSearchButtonProps) {
  const { t } = useTranslation()
  const { state: sidebarState, isMobile } = useSidebar()
  const isCollapsed = sidebarState === "collapsed" && !isMobile

  const [isPopoverOpen, setIsPopoverOpen] = useState(false)
  const [localValue, setLocalValue] = useState(searchValue)
  const [isFocused, setIsFocused] = useState(false)
  const inputRef = useRef<HTMLInputElement>(null)
  const debounceRef = useRef<NodeJS.Timeout | null>(null)

  const isMac = typeof navigator !== "undefined" && navigator.platform.toUpperCase().includes("MAC")

  useEffect(() => {
    setLocalValue(searchValue)
  }, [searchValue])

  useEffect(() => {
    if (isPopoverOpen) {
      setTimeout(() => inputRef.current?.focus(), 0)
    }
  }, [isPopoverOpen])

  const handleInputChange = useCallback(
    (value: string) => {
      setLocalValue(value)
      if (debounceRef.current) {
        clearTimeout(debounceRef.current)
      }
      debounceRef.current = setTimeout(() => {
        onSearchChange(value)
      }, 150)
    },
    [onSearchChange]
  )

  useEffect(() => {
    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current)
        debounceRef.current = null
      }
    }
  }, [])

  const handleClear = useCallback(() => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current)
      debounceRef.current = null
    }
    setLocalValue("")
    onSearchChange("")
    inputRef.current?.focus()
  }, [onSearchChange])

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === "Escape") {
        if (localValue) {
          handleClear()
        } else if (isCollapsed) {
          setIsPopoverOpen(false)
        } else {
          inputRef.current?.blur()
        }
      }
    },
    [localValue, handleClear, isCollapsed]
  )

  useEffect(() => {
    const handleGlobalKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === "k") {
        e.preventDefault()
        if (isCollapsed) {
          setIsPopoverOpen(true)
        } else {
          inputRef.current?.focus()
        }
      }
    }

    window.addEventListener("keydown", handleGlobalKeyDown)
    return () => window.removeEventListener("keydown", handleGlobalKeyDown)
  }, [isCollapsed])

  const shortcutHint = (
    <kbd className="bg-muted text-muted-foreground dark:border-border dark:bg-muted/50 pointer-events-none hidden h-5 items-center gap-1 rounded border px-1.5 text-[10px] font-medium opacity-0 transition-opacity select-none group-focus-within/search:opacity-100 group-hover/search:opacity-100 sm:flex">
      <span className="translate-y-px text-xs leading-none">{isMac ? "⌘" : "Ctrl"}</span>
      <span className="-translate-y-[0.5px] font-sans text-[11px] leading-none">K</span>
    </kbd>
  )

  const trailingControl = (() => {
    if (localValue) {
      return (
        <Button className="size-6 p-0" size="icon" variant="ghost" onClick={handleClear}>
          <X className="size-3" />
        </Button>
      )
    }

    return shortcutHint
  })()

  const searchInput = (
    <div
      className={cn(
        "group/search relative h-9 rounded-lg transition-colors duration-150",
        "hover:bg-sidebar-accent dark:hover:bg-sidebar-accent/30",
        isFocused && "bg-sidebar-accent dark:bg-sidebar-accent/40",
        className
      )}
    >
      <Search
        className="absolute top-1/2 left-2 size-4 -translate-y-1/2 cursor-pointer"
        onClick={() => inputRef.current?.focus()}
      />
      <Input
        ref={inputRef}
        value={localValue}
        className={cn(
          "h-9 rounded-lg border-transparent bg-transparent pr-14 pl-8 text-sm font-semibold shadow-none focus-visible:ring-0",
          isFocused
            ? "placeholder:text-muted-foreground placeholder:font-normal"
            : "placeholder:text-foreground placeholder:font-semibold"
        )}
        placeholder={
          isFocused
            ? t("sidebar.search_placeholder_expanded", {
                defaultValue: "Search projects & chats...",
              })
            : t("sidebar.search_placeholder", { defaultValue: "Search" })
        }
        onBlur={() => setIsFocused(false)}
        onChange={(e) => handleInputChange(e.target.value)}
        onFocus={() => setIsFocused(true)}
        onKeyDown={handleKeyDown}
      />
      <div className="absolute top-1/2 right-2 flex -translate-y-1/2 items-center gap-0.5">
        {trailingControl}
      </div>
    </div>
  )

  if (isCollapsed) {
    return (
      <Popover open={isPopoverOpen} onOpenChange={setIsPopoverOpen}>
        <PopoverTrigger asChild>
          <Button
            size="icon"
            variant="ghost"
            className={cn(
              "size-8 shrink-0",
              searchValue && "bg-sidebar-accent text-sidebar-accent-foreground",
              className
            )}
          >
            <Search className="size-4" />
          </Button>
        </PopoverTrigger>
        <PopoverContent align="start" className="w-64 p-2" side="right" sideOffset={8}>
          {searchInput}
        </PopoverContent>
      </Popover>
    )
  }

  return searchInput
}
